import type { Encoding } from "@crossmint/client-signers";
import {
	decodeBytes,
	type Ed25519,
	encodeBytes,
} from "open-signer-shared-cryptography";
import { secureWipe } from "../../common/secure-memory";
import type {
	CryptoStrategy,
	PublicKey,
	Signature,
} from "../crypto-key-strategy";

export class Ed25519Strategy implements CryptoStrategy<"ed25519"> {
	readonly keyType = "ed25519" as const;

	constructor(private readonly ed25519Service: Ed25519) {}

	async getPrivateKeyFromSeed(seed: Uint8Array): Promise<Uint8Array> {
		const fullSecretKey = this.ed25519Service.secretKeyFromSeed(seed);
		try {
			const privateKey = fullSecretKey.slice(0, 32);
			return privateKey;
		} finally {
			secureWipe(fullSecretKey);
		}
	}

	async getPublicKey(privateKey: Uint8Array): Promise<Uint8Array> {
		const publicKeyBase58 = this.ed25519Service.getPublicKey(privateKey);
		return decodeBytes(publicKeyBase58, "base58");
	}

	async sign(privateKey: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
		return this.ed25519Service.sign(message, privateKey);
	}

	formatPublicKey(
		publicKey: Uint8Array,
		encoding: Encoding = "base58",
	): PublicKey<"ed25519"> {
		return {
			bytes: encodeBytes(publicKey, encoding),
			encoding: encoding,
			keyType: "ed25519",
		};
	}

	formatSignature(
		signature: Uint8Array,
		encoding: Encoding = "base58",
	): Signature<"ed25519"> {
		return {
			bytes: encodeBytes(signature, encoding),
			encoding: encoding,
			keyType: "ed25519",
		};
	}
}
